import React from 'react';

const highlights = [
  {
    title: 'Four Attribute-Critical Units',
    body: (
      <>
        We identify four types of components in safety-aligned LLMs:{' '}
        <strong>ESU</strong>, <strong>EUU</strong>, <strong>CU</strong>, and{' '}
        <strong>RU</strong>.
      </>
    ),
    pill: 'ablation',
  },
  {
    title: 'Freeze 7.5% to Stay Safe',
    body: (
      <>
        Freezing just <strong>7.5%</strong> safety-critical components during
        fine-tuning preserves safety while adapting to new tasks.
      </>
    ),
    pill: 'fine-tuning',
  },
  {
    title: '20% Redundant as Budget',
    body: (
      <>
        Repurposing <strong>20%</strong> redundant units as an{' '}
        <em>alignment budget</em> minimizes alignment tax.
      </>
    ),
    pill: 'efficiency',
  },
];

function AbstractSection() {
  return (
    <section className="py-20 bg-white border-t border-ink-100">
      <div className="max-w-5xl mx-auto px-6">
        <p className="section-eyebrow mb-3">01 &middot; Overview</p>
        <h2 className="section-title">Abstract</h2>

        <div className="mt-10 prose-paper space-y-5 max-w-3xl mx-auto">
          <p>
            As large language models (LLMs) are increasingly integrated into
            real-world applications, ensuring they generate safe and aligned
            responses is a pressing need. Prior work on alignment has largely
            focused on general instruction-following but often overlooked the
            unique properties of safety alignment &mdash; in particular, the
            brittleness of safety mechanisms.
          </p>
          <p>
            To bridge this gap, we propose the{' '}
            <strong>Superficial Safety Alignment Hypothesis (SSAH)</strong>:
            safety alignment teaches an otherwise unsafe model to choose the
            correct reasoning direction &mdash; interpreted as a specialized
            binary classification task &mdash; and incorporates a refusal
            mechanism with multiple reserved fallback options. SSAH implies that
            safety guardrails can be established with just a small number of
            essential components.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {highlights.map((h) => (
            <div
              key={h.title}
              className="card p-6 hover:shadow-card hover:-translate-y-0.5 transition-all duration-200"
            >
              <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-brand-600 bg-brand-50 rounded-full px-2 py-0.5">
                {h.pill}
              </span>
              <h3 className="mt-3 text-lg font-semibold text-ink-900 font-serif">
                {h.title}
              </h3>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">
                {h.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 max-w-3xl mx-auto prose-paper">
          <p>
            Together, these results suggest the{' '}
            <strong>atomic functional unit for safety in LLMs is at the neuron level</strong>,
            and that safety alignment should not be complicated at the surface
            level. We believe this work contributes to the foundation of
            efficient and scalable safety alignment for future LLMs.
          </p>
        </div>
      </div>
    </section>
  );
}

export default AbstractSection;
